// Extracted from MyPlanTab.js during Phase 4 refactor (sprint 2026-06-20).
// Pure presentational modal — reads swap state from parent, dispatches actions
// back via the four handler props (closeSwap / applySwap / regenerateSwap / setSwapState).
import React from 'react';
import { Repeat, X, ChevronDown, Loader2, Sparkles, Check, RefreshCw } from 'lucide-react';

export default function SwapSuggestionModal({ swapState, fr, setSwapState, applySwap, regenerateSwap, closeSwap }) {
  if (!swapState) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={closeSwap}
      data-testid="swap-modal"
    >
      <div
        className="bg-card border border-cyan-500/30 rounded-t-3xl sm:rounded-2xl max-w-md w-full p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 0 60px rgba(34, 211, 238, 0.25)' }}
      >
        <div className="flex items-start gap-2 mb-4">
          <div className="w-10 h-10 rounded-full bg-cyan-500/15 flex items-center justify-center shrink-0">
            <Repeat className="w-5 h-5 text-cyan-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-lg uppercase tracking-tight text-foreground leading-tight">
              {fr ? 'Remplacer l\'exercice' : 'Swap exercise'}
            </h2>
            <p className="text-[11px] text-cyan-300/80 mt-0.5">
              {fr ? '3 alternatives IA — même muscle, ton équipement' : '3 AI alternatives — same muscle, your equipment'}
            </p>
          </div>
          <button
            onClick={closeSwap}
            className="text-muted-foreground hover:text-foreground p-1 shrink-0"
            data-testid="close-swap-modal"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Current exercise card */}
        <div className="rounded-xl bg-secondary/40 border border-border/40 px-3 py-2.5 mb-3" data-testid="swap-current">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-1">
            {fr ? 'Exercice actuel' : 'Current exercise'}
          </p>
          <p className="text-sm font-semibold text-foreground leading-snug line-through opacity-70">
            {swapState.current?.name}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {swapState.current?.sets} × {swapState.current?.reps}
            {swapState.current?.rest_seconds ? ` · ${swapState.current.rest_seconds}s ${fr ? 'repos' : 'rest'}` : ''}
          </p>
        </div>

        {/* Arrow separator */}
        <div className="flex items-center justify-center mb-3" aria-hidden="true">
          <div className="h-px flex-1 bg-cyan-500/20" />
          <ChevronDown className="w-4 h-4 text-cyan-400/60 mx-2" />
          <div className="h-px flex-1 bg-cyan-500/20" />
        </div>

        {/* Suggestion area */}
        {swapState.loading && (
          <div className="flex flex-col items-center justify-center py-8 gap-2" data-testid="swap-loading">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />
            <p className="text-xs text-muted-foreground">{fr ? 'L\'IA réfléchit...' : 'AI thinking...'}</p>
          </div>
        )}

        {!swapState.loading && swapState.error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-3 py-3 text-center" data-testid="swap-error">
            <p className="text-xs text-destructive/90 font-medium">{swapState.error}</p>
            <button
              onClick={regenerateSwap}
              className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 text-[11px] font-bold uppercase tracking-wider transition-colors"
              data-testid="swap-retry"
            >
              <RefreshCw className="w-3 h-3" />
              {fr ? 'Reessayer' : 'Retry'}
            </button>
          </div>
        )}

        {!swapState.loading && !swapState.error && Array.isArray(swapState.suggestions) && swapState.suggestions.length > 0 && (
          <div className="space-y-2 mb-1" data-testid="swap-suggestion">
            <p className="text-[9px] uppercase tracking-wider text-cyan-300 font-bold flex items-center gap-1 px-1">
              <Sparkles className="w-3 h-3" />
              {fr
                ? `${swapState.suggestions.length} alternative${swapState.suggestions.length > 1 ? 's' : ''} proposee${swapState.suggestions.length > 1 ? 's' : ''}`
                : `${swapState.suggestions.length} AI alternative${swapState.suggestions.length > 1 ? 's' : ''}`}
              <span className="ml-auto text-[9px] font-normal text-cyan-300/70 normal-case tracking-normal">
                {fr ? 'Sélectionne ton choix' : 'Pick your choice'}
              </span>
            </p>
            {swapState.suggestions.map((sug, idx) => {
              const selected = swapState.selectedIdx === idx;
              return (
                <button
                  key={`${sug.name}-${idx}`}
                  type="button"
                  onClick={() => setSwapState(s => s ? { ...s, selectedIdx: idx } : s)}
                  disabled={swapState.applying}
                  className={`w-full text-left rounded-xl border px-3 py-2.5 transition-all active:scale-[0.99] ${
                    selected
                      ? 'bg-cyan-500/15 border-cyan-400 ring-2 ring-cyan-400/40 shadow-md'
                      : 'bg-cyan-500/5 border-cyan-500/25 hover:bg-cyan-500/10 hover:border-cyan-500/40'
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                  data-testid={`swap-option-${idx}`}
                  aria-pressed={selected}
                >
                  <div className="flex items-start gap-2">
                    <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? 'bg-cyan-400 border-cyan-300' : 'border-cyan-500/40'}`}>
                      {selected && <Check className="w-3 h-3 text-cyan-950" strokeWidth={3} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-bold leading-snug ${selected ? 'text-foreground' : 'text-foreground/90'}`}
                        data-testid={idx === 0 ? 'swap-suggested-name' : `swap-suggested-name-${idx}`}
                      >
                        {sug.name}
                      </p>
                      <p className="text-[10.5px] text-cyan-200/80 mt-0.5 font-medium">
                        {sug.sets} × {sug.reps}
                        {sug.rest_seconds ? ` · ${sug.rest_seconds}s ${fr ? 'repos' : 'rest'}` : ''}
                      </p>
                      {sug.notes && (
                        <p className="text-[10.5px] text-foreground/80 italic mt-1.5 leading-snug border-l-2 border-cyan-400/40 pl-2">
                          {sug.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Action buttons */}
        {!swapState.loading && !swapState.error && Array.isArray(swapState.suggestions) && swapState.suggestions.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              onClick={applySwap}
              disabled={swapState.applying}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-cyan-950 font-bold uppercase tracking-wider text-xs disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 transition-all"
              data-testid="swap-accept"
            >
              {swapState.applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {fr ? 'Accepter ce choix' : 'Accept this choice'}
            </button>
            <button
              onClick={regenerateSwap}
              disabled={swapState.applying}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-cyan-500/40 hover:bg-cyan-500/10 text-cyan-300 font-bold uppercase tracking-wider text-xs disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 transition-all"
              data-testid="swap-regenerate"
              title={fr ? 'Demander 3 nouvelles propositions' : 'Get 3 new proposals'}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {fr ? 'Autres' : 'Others'}
            </button>
            <button
              onClick={closeSwap}
              disabled={swapState.applying}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/50 font-bold uppercase tracking-wider text-xs disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 transition-all"
              data-testid="swap-cancel"
            >
              {fr ? 'Annuler' : 'Cancel'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
